// API Service for Tap Data - POS Ready
// This stub simulates a POS system API that can be easily replaced with real endpoints

// Mock data for development - will be replaced with real POS API calls
const mockTapData = [
  {
    tapNumber: 1,
    beerName: "Pale Warrior",
    breweryName: "Hawkers Beer",
    style: "Pale Ale",
    abv: 4.8,
    tastingNotes: "Citrus forward with tropical fruit notes and a clean bitter finish",
    basePriceCategory: "premium",
    isAvailable: true,
    lastUpdated: Date.now()
  },
  {
    tapNumber: 2,
    beerName: "Hazy Days",
    breweryName: "Moon Dog Brewery",
    style: "Hazy",
    abv: 5.2,
    tastingNotes: "Juicy and soft with stone fruit and citrus, low bitterness",
    basePriceCategory: "standard",
    isAvailable: true,
    lastUpdated: Date.now()
  },
  {
    tapNumber: 3,
    beerName: "Carlton Draught",
    breweryName: "Carlton & United Breweries",
    style: "Lager",
    abv: 4.6,
    tastingNotes: "Clean, crisp lager with subtle malt sweetness and gentle hop character",
    basePriceCategory: "standard",
    isAvailable: true,
    lastUpdated: Date.now()
  },
  {
    tapNumber: 4,
    beerName: "Furphy Refreshing Ale",
    breweryName: "Furphy",
    style: "Pale Ale",
    abv: 4.4,
    tastingNotes: "Balanced and refreshing with subtle hop aroma and malt backbone",
    basePriceCategory: "standard",
    isAvailable: true,
    lastUpdated: Date.now()
  },
  {
    tapNumber: 5,
    beerName: "Cloudy Apple",
    breweryName: "Napoleone Brewery",
    style: "Cider",
    abv: 4.8,
    tastingNotes: "Natural apple flavour with a refreshing, crisp finish",
    basePriceCategory: "standard",
    isAvailable: true,
    lastUpdated: Date.now()
  },
  {
    tapNumber: 6,
    beerName: "Coffee Porter",
    breweryName: "Bentspoke Brewing",
    style: "Stout",
    abv: 5.8,
    tastingNotes: "Rich coffee aroma with chocolate notes and smooth roasted finish",
    basePriceCategory: "premium",
    isAvailable: true,
    lastUpdated: Date.now()
  },
  {
    tapNumber: 7,
    beerName: "Newtowner",
    breweryName: "Young Henrys",
    style: "Pale Ale",
    abv: 4.8,
    tastingNotes: "Tropical fruit hop character with citrus zest and clean malt base",
    basePriceCategory: "premium",
    isAvailable: true,
    lastUpdated: Date.now()
  },
  {
    tapNumber: 8,
    beerName: "Great Northern",
    breweryName: "Great Northern Brewing Co",
    style: "Lager",
    abv: 3.5,
    tastingNotes: "Light, crisp and easy drinking with minimal hop bitterness",
    basePriceCategory: "standard",
    isAvailable: true,
    lastUpdated: Date.now()
  },
  {
    tapNumber: 9,
    beerName: "Juicy IPA",
    breweryName: "Balter Brewing",
    style: "Hazy",
    abv: 6.8,
    tastingNotes: "Intensely hoppy with mango and passionfruit, soft mouthfeel",
    basePriceCategory: "premium",
    isAvailable: true,
    lastUpdated: Date.now()
  },
  {
    tapNumber: 10,
    beerName: "Pear Cider",
    breweryName: "Batlow Premium Ciders",
    style: "Cider",
    abv: 4.5,
    tastingNotes: "Fresh pear character with crisp acidity and natural sweetness",
    basePriceCategory: "standard",
    isAvailable: true,
    lastUpdated: Date.now()
  },
  {
    tapNumber: 11,
    beerName: "XPA",
    breweryName: "Stone & Wood",
    style: "Pale Ale",
    abv: 4.4,
    tastingNotes: "Clean and fresh with Galaxy hop aromatics and balanced bitterness",
    basePriceCategory: "premium",
    isAvailable: true,
    lastUpdated: Date.now()
  },
  {
    tapNumber: 12,
    beerName: "Coming Soon",
    breweryName: "TBA",
    style: "TBA",
    abv: 0,
    tastingNotes: "Something special is coming to Tap 12...",
    basePriceCategory: "standard",
    isAvailable: false,
    lastUpdated: Date.now()
  }
];

// Pricing logic - Australian standard glass sizes
const glassSizes = {
  pot: { size: 285, name: 'Pot', unit: 'ml' },
  schooner: { size: 425, name: 'Schooner', unit: 'ml' },
  pint: { size: 570, name: 'Pint', unit: 'ml' }
};

// Base pricing structure (can be configured per venue)
const basePricing = {
  standard: {
    pot: 8.50,
    schooner: 13.50,
    pint: 14.50
  },
  premium: {
    pot: 10.00,
    schooner: 15.00,
    pint: 16.00
  }
};

// Style-based pricing adjustments
const stylePricing = {
  'Hazy': { pot: 0, schooner: 0, pint: 0 }, // Base pricing
  'Pale Ale': { pot: 1.50, schooner: 1.50, pint: 1.50 }, // +$1.50
  'Cider': { pot: -1.50, schooner: -1.50, pint: -1.50 }, // -$1.50
  'Lager': { pot: 0, schooner: 0, pint: 0 }, // Base pricing
  'Stout': { pot: 1.00, schooner: 1.00, pint: 1.00 }, // +$1.00
  'TBA': { pot: 0, schooner: 0, pint: 0 } // Base pricing
};

// Calculate price for a beer based on style and size
export const calculatePrice = (beer, glassSize) => {
  if (!beer.isAvailable) return null;
  
  const basePrice = basePricing[beer.basePriceCategory][glassSize];
  const styleAdjustment = stylePricing[beer.style][glassSize] || 0;
  
  return (basePrice + styleAdjustment).toFixed(2);
};

// Get all glass sizes with prices for a beer
export const getBeerPricing = (beer) => {
  const pricing = {};
  
  Object.keys(glassSizes).forEach(size => {
    pricing[size] = {
      ...glassSizes[size],
      price: calculatePrice(beer, size),
      available: beer.isAvailable
    };
  });
  
  return pricing;
};

// API Functions - these will be replaced with real API calls

// Fetch all tap data
export const fetchTaps = async () => {
  try {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // In production, this would be:
    // const response = await fetch('/api/taps');
    // return await response.json();
    
    return {
      success: true,
      data: mockTapData,
      lastUpdated: Date.now(),
      totalTaps: 12
    };
  } catch (error) {
    console.error('Error fetching tap data:', error);
    return {
      success: false,
      error: 'Failed to fetch tap data',
      data: []
    };
  }
};

// Fetch single tap data
export const fetchTap = async (tapNumber) => {
  try {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const tap = mockTapData.find(t => t.tapNumber === tapNumber);
    
    if (!tap) {
      return {
        success: false,
        error: 'Tap not found',
        data: null
      };
    }
    
    return {
      success: true,
      data: tap
    };
  } catch (error) {
    console.error('Error fetching tap:', error);
    return {
      success: false,
      error: 'Failed to fetch tap data',
      data: null
    };
  }
};

// Update tap data (for admin use)
export const updateTap = async (tapNumber, updateData) => {
  try {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const tapIndex = mockTapData.findIndex(t => t.tapNumber === tapNumber);
    
    if (tapIndex === -1) {
      return {
        success: false,
        error: 'Tap not found'
      };
    }
    
    mockTapData[tapIndex] = {
      ...mockTapData[tapIndex],
      ...updateData,
      lastUpdated: Date.now()
    };
    
    return {
      success: true,
      data: mockTapData[tapIndex]
    };
  } catch (error) {
    console.error('Error updating tap:', error);
    return {
      success: false,
      error: 'Failed to update tap data'
    };
  }
};

// Get available glass sizes
export const getGlassSizes = () => glassSizes;

// Get pricing structure
export const getPricingStructure = () => ({
  basePricing,
  stylePricing,
  glassSizes
});